import React, { useState } from 'react';
import { Modal, Button, InputNumber } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import './BuyCreditsModal.scss';

const BuyCreditsModal = ({
    visible,
    onClose,
    onConfirm, 
    planData
}) => {
    const showFullCatalog = !!planData && planData.name !== 'custom_contract_ai' && planData.name !== 'enterprise';

    const creditItems = showFullCatalog ? {
        products: [
            { id: 'product_model', name: 'Product models', price: 125, credits: 895, subtitle: '895 credits per model' },
            { id: 'simple_product_variants', name: 'Product variants', price: 63, credits: 450, subtitle: '450 credits per model' },
            { id: 'space_room_models', name: 'Space/room models', price: 250, credits: 1785, subtitle: '1785 credits per model' },
            { id: 'ai_models', name: 'AI models', price: 10, credits: 70, subtitle: '70 credits per model' }
        ],
        images: [
            { id: 'ai_image_1k', name: 'AI Images', price: 0.7, credits: 5, subtitle: '5 credits per image' },
            { id: 'ai_video', name: 'AI Video', price: 6, credits: 45, subtitle: '45 credits per video' },
            { id: 'silo_images_4k', name: 'Silo images (4K)', price: 5, credits: 35, subtitle: '35 credits per set', custom_description: 'set of 4 images' },
            { id: 'lifestyle_images_2k', name: 'Lifestyle images (2K)', price: 30, credits: 215, subtitle: '215 credits per image' },
            { id: 'lifestyle_images_4k', name: 'Lifestyle images (4K)', price: 60, credits: 430, subtitle: '430 credits per image' }
        ],
        otherAssets: [
            { id: '360_spins', name: '360 spins', price: 65, credits: 465, subtitle: '465 credits per asset' },
            { id: 'mp4_videos', name: 'MP4 videos', price: 65, credits: 465, subtitle: '465 credits per asset' },
            { id: '3d_tours', name: '3D tours', price: 65, credits: 465, subtitle: '465 credits per asset' }
        ]
    } : {
        images: [
            { id: 'ai_image_1k', name: 'AI Images', price: 0.7, credits: 5, subtitle: '5 credit per image' },
            { id: 'ai_video', name: 'AI Video', price: 6, credits: 45, subtitle: '45 credits per video' }
        ]
    };

    const allItemsList = showFullCatalog ? [...creditItems.products, ...creditItems.images, ...creditItems.otherAssets] : [...creditItems.images];

    const [quantities, setQuantities] = useState({});

    const getEffectiveQuantity = (itemId) => {
        const item = allItemsList.find(i => i.id === itemId);
        const qty = quantities[itemId];
        if (qty !== undefined) return qty;
        return item?.minimum || 0;
    };

    const handleQuantityChange = (id, value) => {
        setQuantities(prev => ({
            ...prev,
            [id]: value || 0
        }));
    };

    const calculateTotal = () => {
        let totalCredits = 0;
        let totalPrice = 0;

        allItemsList.forEach(item => {
            const qty = getEffectiveQuantity(item.id);
            if (qty > 0) {
                totalCredits += item.credits * qty;
                totalPrice += item.price * qty;
            }
        });
        totalPrice = parseFloat(totalPrice.toFixed(2))

        return { totalCredits, totalPrice };
    };

    const { totalCredits, totalPrice } = calculateTotal();

    const renderCreditItem = (item) => {
        const quantity = getEffectiveQuantity(item.id);
        const itemCredits = item.credits * quantity;
        const itemPrice = item.price * quantity;

        return (
            <div key={item.id} className='credit-item'>
                <div className='credit-item-left'>
                    <div className='manrope f-14 w-700 lh-22 black-085'>{item.name}</div>
                    <div className='justify-in-start align-baseline'>
                        <span className='manrope f-26 w-700 lh-40 black-085'>${item.price.toLocaleString()}</span>
                        <span className='manrope f-16 w-500 lh-40 black-085'>
                            /{item.custom_description ? item.custom_description : item.name.toLowerCase().includes('model') ? 'model' : item.name.toLowerCase().includes('image') ? 'image' : 'asset'}
                        </span>
                    </div>
                    <div className='manrope f-12 w-400 lh-20 black-045'>{item.subtitle}</div>
                </div>
                <div className='credit-item-right'>
                    <div className='quantity-section'>
                        <div className='manrope f-10 w-500 lh-22 black-085 mb-4'>QTY</div>
                        <InputNumber
                            min={item.minimum || 0}
                            value={quantity}
                            onChange={(value) => handleQuantityChange(item.id, value)}
                            className='quantity-input manrope f-14 lh-22 black-085 w-400'
                        />
                    </div>
                    <div className='credits-price-section'>
                        <div className='manrope f-14 w-400 lh-22 black-085' style={{ opacity: 0.6 }}>
                            {itemCredits} credits
                        </div>
                        <div className='manrope f-14 w-500 lh-22 black-085'>
                            ${itemPrice.toFixed(2)}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Modal
            centered
            closable={false}
            maskClosable={false}
            visible={visible}
            onCancel={onClose}
            footer={null}
            className="buy-credits-modal"
            destroyOnClose={true}
            width={700}>

            <div className="buy-credits-container">
                <div className="buy-credits-header">
                    <div className="modal-close-btn" onClick={onClose}>
                        <CloseOutlined />
                    </div>
                    <div className="manrope f-20 w-500 black-085 lh-28">
                        Buy credits
                    </div>
                </div>

                <div className="buy-credits-content">
                    {showFullCatalog &&
                    <div className="credit-section">
                        <div className="manrope f-14 w-700 lh-22 black-085 section-title">Products</div>
                        {creditItems.products.map(renderCreditItem)}
                    </div>}

                    <div className="credit-section">
                        <div className="manrope f-14 w-700 lh-22 black-085 section-title">Images</div>
                        {creditItems.images.map(renderCreditItem)}
                    </div>

                    {showFullCatalog && <div className="credit-section">
                        <div className="manrope f-14 w-700 lh-22 black-085 section-title">Other Assets</div>
                        {creditItems.otherAssets.map(renderCreditItem)}
                    </div>}
                </div>

                <div className="buy-credits-footer">
                    <div className="footer-content">
                        {totalCredits > 0 && (
                            <div className="subtotal">
                                <span className="manrope f-16 w-500 lh-24 black-085">Subtotal:</span>
                                <span className="manrope f-20 w-700 lh-28 black-085">${totalPrice.toLocaleString()}</span>
                            </div>
                        )}
                        <div className="footer-actions">
                            <Button
                                className="cancel-button"
                                onClick={onClose}>
                                Cancel
                            </Button>
                            <Button
                                className="confirm-button"
                                onClick={() => {
                                    if (onConfirm) {
                                        const effectiveQuantities = {};
                                        allItemsList.forEach(item => {
                                            const qty = getEffectiveQuantity(item.id);
                                            if (qty > 0) {
                                                effectiveQuantities[item.id] = qty;
                                            }
                                        });
                                        console.log('BuyCreditsModal: Confirm clicked', { quantities: effectiveQuantities, totalCredits, totalPrice });
                                        onConfirm({ quantities: effectiveQuantities, totalCredits, totalPrice });
                                    } else {
                                        console.log('BuyCreditsModal: onConfirm not provided');
                                    }
                                }}
                                disabled={totalCredits < 20}>
                                {totalCredits >= 20 ? `Buy ${totalCredits} credits` : totalCredits > 0 ? 'Minimum 20 credits required' : 'Add credits to continue'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default BuyCreditsModal;
